import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  List,
  ListItemButton,
  Divider,
  Chip,
  Stack,
  Button,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../components/PageHeader/PageHeader';
import CoreBillingService from '../../../services/CoreBillingService';
import {
  buildMonthlyBillingPeriods,
  formatSubscriptionDate,
  MONTHLY_STATUS_OPTIONS
} from './billingPeriods';
import { formatMoney } from '../../../utils/currency';

const MONTHLY_PERIOD_PAGE_SIZES = [12, 24, 48];

const getSubscriptionStatusBadge = (statusRaw) => {
  const status = (statusRaw || '').toUpperCase();
  switch (status) {
    case 'ACTIVE':
      return { status, color: 'success' };
    case 'PENDING':
      return { status, color: 'warning' };
    case 'PAUSED':
      return { status, color: 'warning' };
    case 'CANCELED':
    case 'REJECTED_FINAL':
      return { status, color: 'error' };
    default:
      return { status: status || 'UNKNOWN', color: 'default' };
  }
};

const getInvoiceStatusBadge = (statusRaw) => {
  const status = (statusRaw || '').toUpperCase();
  switch (status) {
    case 'PAID':
    case 'ACTIVE':
      return { status, color: 'success' };
    case 'PENDING':
    case 'PAUSED':
      return { status, color: 'warning' };
    case 'CANCELED':
    case 'REJECTED':
    case 'REJECTED_FINAL':
      return { status, color: 'error' };
    default:
      return { status: status || 'UNKNOWN', color: 'default' };
  }
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  return (
    responseData?.error_message ||
    responseData?.message ||
    (typeof responseData?.error === 'string' ? responseData.error : null) ||
    (error?.message && error.message !== 'Request failed with status code 400' ? error.message : null) ||
    fallbackMessage
  );
};

// The native date field works with YYYY-MM-DD, while older account data can
// arrive with a time component. Compare and persist the same canonical value
// so changing only the date always marks the form as modified.
const normalizeSubscriptionStartDate = (value) => (value || '').slice(0, 10);

const ChargesPage = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [selectedBillingAccountId, setSelectedBillingAccountId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [detailTab, setDetailTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savingBillingAccount, setSavingBillingAccount] = useState(false);
  const [sendingSubscriptionLink, setSendingSubscriptionLink] = useState(false);
  const [creatingSubscriptionLink, setCreatingSubscriptionLink] = useState(false);
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  const [generatingInvoiceId, setGeneratingInvoiceId] = useState(null);
  const [generatingExtrasLink, setGeneratingExtrasLink] = useState(false);
  const [markingInvoiceId, setMarkingInvoiceId] = useState(null);
  const [syncingInvoiceId, setSyncingInvoiceId] = useState(null);
  const [syncingMercadoPagoStatuses, setSyncingMercadoPagoStatuses] = useState(false);
  const [manualLinkDialogOpen, setManualLinkDialogOpen] = useState(false);
  const [manualLinkPreapprovalId, setManualLinkPreapprovalId] = useState('');
  const [manualLinkSubmitting, setManualLinkSubmitting] = useState(false);
  const [paymentLookupId, setPaymentLookupId] = useState('');
  const [paymentLookupLoading, setPaymentLookupLoading] = useState(false);
  const [paymentLookupResult, setPaymentLookupResult] = useState(null);
  const [cobrosViewMode, setCobrosViewMode] = useState('company');
  const [monthlyOverviewYear, setMonthlyOverviewYear] = useState(() => new Date().getFullYear());
  const [monthlyOverviewMonth, setMonthlyOverviewMonth] = useState(() => new Date().getMonth() + 1);
  const [monthlyOverviewRows, setMonthlyOverviewRows] = useState([]);
  const [loadingMonthlyOverview, setLoadingMonthlyOverview] = useState(false);
  const [openingInvoiceId, setOpeningInvoiceId] = useState(null);
  const [invoiceMenuAnchorEl, setInvoiceMenuAnchorEl] = useState(null);
  const [invoiceMenuInvoiceId, setInvoiceMenuInvoiceId] = useState(null);
  const [monthlyPeriodsPage, setMonthlyPeriodsPage] = useState(0);
  const [monthlyPeriodsPageSize, setMonthlyPeriodsPageSize] = useState(12);
  const [statusCorrection, setStatusCorrection] = useState(null);
  const [savingStatusCorrection, setSavingStatusCorrection] = useState(false);
  const [subscriptionPriceDraft, setSubscriptionPriceDraft] = useState('');
  const [subscriptionStartDateDraft, setSubscriptionStartDateDraft] = useState('');
  const [billingEmailDraft, setBillingEmailDraft] = useState('');
  const [fiscalRutDraft, setFiscalRutDraft] = useState('');
  const [fiscalBusinessNameDraft, setFiscalBusinessNameDraft] = useState('');
  const [fiscalAddressDraft, setFiscalAddressDraft] = useState('');
  const [commercialAgreementNotesDraft, setCommercialAgreementNotesDraft] = useState('');
  const [benefitAmountDraft, setBenefitAmountDraft] = useState('');
  const [benefitMonthlyLimitDraft, setBenefitMonthlyLimitDraft] = useState('');
  const [benefitDescriptionDraft, setBenefitDescriptionDraft] = useState('');
  const [benefitReasonDraft, setBenefitReasonDraft] = useState('NEW_BUSINESS_UNIT');
  const [grantingBenefit, setGrantingBenefit] = useState(false);
  const [subscriptionCheckoutUrl, setSubscriptionCheckoutUrl] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleGrantBenefit = async () => {
    const amount = Number(benefitAmountDraft);
    const monthlyLimit = benefitMonthlyLimitDraft === '' ? null : Number(benefitMonthlyLimitDraft);
    if (!selectedBillingAccountId || !Number.isFinite(amount) || amount <= 0 || (monthlyLimit != null && (!Number.isFinite(monthlyLimit) || monthlyLimit <= 0))) {
      setSnackbar({ open: true, message: 'Ingresá un beneficio y un límite mensual válidos.', severity: 'error' });
      return;
    }
    setGrantingBenefit(true);
    try {
      await CoreBillingService.grantBillingBenefit(selectedBillingAccountId, {
        amount,
        monthlyLimit,
        benefitReason: benefitReasonDraft,
        description: benefitDescriptionDraft.trim() || null
      });
      setBenefitAmountDraft('');
      setBenefitDescriptionDraft('');
      await handleSelectBillingAccount(selectedBillingAccountId, true);
      setSnackbar({ open: true, message: 'Beneficio otorgado. Se actualizó el próximo cobro si la suscripción está activa.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'No se pudo otorgar el beneficio.'), severity: 'error' });
    } finally {
      setGrantingBenefit(false);
    }
  };

  const getStatusLabel = (statusRaw) => {
    const status = (statusRaw || '').toUpperCase();
    switch (status) {
      case 'ACTIVE':
        return t('core_status_active');
      case 'PENDING':
        return t('core_status_pending');
      case 'PAID':
        return t('core_status_paid');
      case 'PAUSED':
        return t('core_status_paused');
      case 'CANCELED':
        return t('core_status_canceled');
      case 'REJECTED':
        return t('core_status_rejected');
      case 'REJECTED_FINAL':
        return t('core_status_rejected_final');
      case 'MANUAL_COLLECTION_OPEN':
        return t('core_status_manual_collection_open');
      case 'SIN_REGISTRO':
        return 'Sin registro';
      default:
        return status || t('core_status_unknown');
    }
  };

  const formatMovementDateTime = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('es-UY', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const showToast = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchCompanies = async () => {
    try {
      const data = await CoreBillingService.getCompanies(search);
      setCompanies(Array.isArray(data) ? data : []);
      return data;
    } catch (e) {
      console.error('Error fetching core billing companies:', e);
      setCompanies([]);
      showToast(
        getApiErrorMessage(
          e,
          t('core_charges_error_load_companies', { defaultValue: 'Error cargando empresas' })
        ),
        'error'
      );
      return [];
    }
  };

  const fetchDetail = async (companyId) => {
    try {
      const data = await CoreBillingService.getCompanyDetail(companyId);
      return data;
    } catch (e) {
      console.error('Error fetching core billing detail:', e);
      showToast(
        getApiErrorMessage(
          e,
          t('core_charges_error_load_detail', { defaultValue: 'Error cargando detalle de cobros' })
        ),
        'error'
      );
      return null;
    }
  };

  const fetchTransactions = async (billingAccountId) => {
    try {
      const data = await CoreBillingService.getBillingAccountTransactions(billingAccountId);
      setTransactions(Array.isArray(data) ? data : []);
      return data;
    } catch (e) {
      console.error('Error fetching core billing transactions:', e);
      setTransactions([]);
      showToast(
        getApiErrorMessage(
          e,
          t('core_charges_error_load_transactions', { defaultValue: 'Error cargando movimientos' })
        ),
        'error'
      );
      return [];
    }
  };

  const normalizeCompanyDetail = (data, accountId) => ({
    companyId: data.companyId,
    companyName: data.companyName,
    subscriptionStatus: data.subscriptionStatus,
    mpPreapprovalId: data.mpPreapprovalId,
    syncHealth: data.syncHealth || 'OK',
    syncHealthDetail: data.syncHealthDetail || null,
    lastReconcileCheckAt: data.lastReconcileCheckAt || null,
    invoices: data.invoices || [],
    extraInvoices: data.extraInvoices || [],
    paymentLinks: data.paymentLinks || [],
    billingAccount: (data.billingAccounts || []).find((account) => account.id === accountId) || null
  });

  // Declarada acá arriba (no más abajo, junto a handleSelectCompany) porque el efecto de
  // reconciliación de más abajo la referencia en su lista de dependencias -- estando después,
  // el "const" todavía no se había inicializado en ese punto del render y tiraba
  // "Cannot access 'handleSelectBillingAccount' before initialization".
  const handleSelectBillingAccount = async (billingAccountId, forceRefresh = false) => {
    if (!billingAccountId || (!forceRefresh && billingAccountId === selectedBillingAccountId)) return;
    setSelectedBillingAccountId(billingAccountId);
    const [accountDetail] = await Promise.all([
      CoreBillingService.getBillingAccountDetail(billingAccountId),
      fetchTransactions(billingAccountId)
    ]);
    if (!accountDetail) return;
    setDetail(accountDetail);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(fetchCompanies, 250);
    return () => clearTimeout(timeout);
  }, [search]);

  // Loads (or refreshes) the monthly overview each time that tab becomes active, mirroring
  // what the old "Vista mensual" button used to do on open.
  useEffect(() => {
    if (cobrosViewMode === 'monthly') {
      handleLoadMonthlyOverview();
    }
  }, [cobrosViewMode]);


  const handleSelectCompany = async (company) => {
    setSelectedCompany(company);
    setDetailTab(0);
    const companyDetail = await fetchDetail(company.companyId);
    if (!companyDetail) {
      setDetail(null);
      setBillingAccounts([]);
      setSelectedBillingAccountId(null);
      setTransactions([]);
      return;
    }
    const accounts = companyDetail?.billingAccounts || [];
    const nextBillingAccountId = companyDetail?.selectedBillingAccountId || accounts[0]?.id || null;
    setBillingAccounts(accounts);
    setSelectedBillingAccountId(nextBillingAccountId);
    if (!nextBillingAccountId) {
      setDetail(normalizeCompanyDetail(companyDetail, null));
      setTransactions([]);
      return;
    }
    setDetail(normalizeCompanyDetail(companyDetail, nextBillingAccountId));
    await fetchTransactions(nextBillingAccountId);
  };

  const currentInvoices = detail?.invoices || [];
  const selectedBillingAccount = useMemo(
    () => billingAccounts.find((account) => account.id === selectedBillingAccountId) || detail?.billingAccount || null,
    [billingAccounts, selectedBillingAccountId, detail]
  );
  const normalizedStoredBillingEmail = (selectedBillingAccount?.billingEmail || '').trim();
  const normalizedDraftBillingEmail = billingEmailDraft.trim();
  const normalizedStoredFiscalRut = (selectedBillingAccount?.fiscalRut || '').trim();
  const normalizedDraftFiscalRut = fiscalRutDraft.trim();
  const normalizedStoredFiscalBusinessName = (selectedBillingAccount?.fiscalBusinessName || '').trim();
  const normalizedDraftFiscalBusinessName = fiscalBusinessNameDraft.trim();
  const normalizedStoredFiscalAddress = (selectedBillingAccount?.fiscalAddress || '').trim();
  const normalizedDraftFiscalAddress = fiscalAddressDraft.trim();
  const normalizedStoredCommercialAgreementNotes = (selectedBillingAccount?.commercialAgreementNotes || '').trim();
  const normalizedDraftCommercialAgreementNotes = commercialAgreementNotesDraft.trim();
  const normalizedStoredSubscriptionPrice =
    selectedBillingAccount?.subscriptionPrice == null ? '' : String(selectedBillingAccount.subscriptionPrice);
  const normalizedDraftSubscriptionPrice = subscriptionPriceDraft.trim();
  const normalizedStoredSubscriptionStartDate = normalizeSubscriptionStartDate(selectedBillingAccount?.subscriptionStartDate);
  const normalizedDraftSubscriptionStartDate = normalizeSubscriptionStartDate(subscriptionStartDateDraft);
  const parsedSubscriptionPrice =
    normalizedDraftSubscriptionPrice === '' ? null : Number(normalizedDraftSubscriptionPrice);
  const subscriptionPriceInvalid =
    normalizedDraftSubscriptionPrice !== '' &&
    (!Number.isFinite(parsedSubscriptionPrice) || parsedSubscriptionPrice < 0);
  const billingEmailInvalid = normalizedDraftBillingEmail !== '' && !/^\S+@\S+\.\S+$/.test(normalizedDraftBillingEmail);
  const subscriptionAccepted = (detail?.subscriptionStatus || '').toUpperCase() !== 'PENDING';
  const subscriptionAlreadyAttached = ['ACTIVE', 'PAUSED'].includes((detail?.subscriptionStatus || '').toUpperCase());
  const accountSupportsSubscription = selectedBillingAccount?.subscriptionEnabled !== false;
  const accountSupportsExtras = selectedBillingAccount?.extrasEnabled !== false;
  const hasValidSubscriptionPrice =
    parsedSubscriptionPrice != null &&
    Number.isFinite(parsedSubscriptionPrice) &&
    parsedSubscriptionPrice > 0;
  const canSaveBillingAccount =
    selectedBillingAccountId &&
    !savingBillingAccount &&
    !subscriptionPriceInvalid &&
    !billingEmailInvalid &&
    (
      normalizedDraftSubscriptionPrice !== normalizedStoredSubscriptionPrice ||
      normalizedDraftSubscriptionStartDate !== normalizedStoredSubscriptionStartDate ||
      normalizedDraftBillingEmail !== normalizedStoredBillingEmail ||
      normalizedDraftFiscalRut !== normalizedStoredFiscalRut ||
      normalizedDraftFiscalBusinessName !== normalizedStoredFiscalBusinessName ||
      normalizedDraftFiscalAddress !== normalizedStoredFiscalAddress ||
      normalizedDraftCommercialAgreementNotes !== normalizedStoredCommercialAgreementNotes
    );
  const canUseSubscriptionPlanActions =
    selectedBillingAccountId &&
    !saving &&
    !savingBillingAccount &&
    !updatingSubscription &&
    !sendingSubscriptionLink &&
    !creatingSubscriptionLink &&
    !canSaveBillingAccount &&
    !subscriptionAlreadyAttached &&
    accountSupportsSubscription &&
    !subscriptionPriceInvalid &&
    !billingEmailInvalid &&
    hasValidSubscriptionPrice;
  const canSendSubscriptionLink =
    canUseSubscriptionPlanActions &&
    Boolean(normalizedDraftBillingEmail);
  const canCreateSubscriptionLink = canUseSubscriptionPlanActions;
  const invoicesById = useMemo(
    () => Object.fromEntries(currentInvoices.map((inv) => [inv.id, inv])),
    [currentInvoices]
  );
  const pendingInvoices = useMemo(
    () => currentInvoices.filter((it) => it.status !== 'PAID'),
    [currentInvoices]
  );
  const openPaymentLinks = useMemo(
    () =>
      (detail?.paymentLinks || []).filter((link) => {
        const isOpen = (link.status || '').toUpperCase() === 'OPEN';
        if (!isOpen) return false;
        if (!link.invoiceId) return true;
        const linkedInvoice = invoicesById[link.invoiceId];
        return !linkedInvoice || (linkedInvoice.status || '').toUpperCase() !== 'PAID';
      }),
    [detail?.paymentLinks, invoicesById]
  );
  const pendingExtraInvoices = useMemo(
    () => (detail?.extraInvoices || []).filter((it) => (it.status || '').toUpperCase() !== 'PAID'),
    [detail?.extraInvoices]
  );
  const monthlyPeriods = useMemo(
    () => buildMonthlyBillingPeriods(
      currentInvoices,
      selectedBillingAccount?.subscriptionStartDate || selectedBillingAccount?.createdAt
    ),
    [currentInvoices, selectedBillingAccount?.subscriptionStartDate, selectedBillingAccount?.createdAt]
  );
  const paginatedMonthlyPeriods = useMemo(
    () => monthlyPeriods.slice(
      monthlyPeriodsPage * monthlyPeriodsPageSize,
      monthlyPeriodsPage * monthlyPeriodsPageSize + monthlyPeriodsPageSize
    ),
    [monthlyPeriods, monthlyPeriodsPage, monthlyPeriodsPageSize]
  );
  useEffect(() => {
    setSubscriptionPriceDraft(
      selectedBillingAccount?.subscriptionPrice == null ? '' : String(selectedBillingAccount.subscriptionPrice)
    );
    setSubscriptionStartDateDraft(normalizeSubscriptionStartDate(selectedBillingAccount?.subscriptionStartDate));
    setBillingEmailDraft(selectedBillingAccount?.billingEmail || '');
    setFiscalRutDraft(selectedBillingAccount?.fiscalRut || '');
    setFiscalBusinessNameDraft(selectedBillingAccount?.fiscalBusinessName || '');
    setFiscalAddressDraft(selectedBillingAccount?.fiscalAddress || '');
    setCommercialAgreementNotesDraft(selectedBillingAccount?.commercialAgreementNotes || '');
    setSubscriptionCheckoutUrl('');
    setMonthlyPeriodsPage(0);
  }, [
    selectedBillingAccount?.id,
    selectedBillingAccount?.subscriptionPrice,
    selectedBillingAccount?.subscriptionStartDate,
    selectedBillingAccount?.billingEmail,
    selectedBillingAccount?.fiscalRut,
    selectedBillingAccount?.fiscalBusinessName,
    selectedBillingAccount?.fiscalAddress,
    selectedBillingAccount?.commercialAgreementNotes
  ]);

  const refreshCurrentSelection = async () => {
    if (!selectedCompany) return;

    if (selectedBillingAccountId) {
      const [accountDetail] = await Promise.all([
        CoreBillingService.getBillingAccountDetail(selectedBillingAccountId),
        fetchTransactions(selectedBillingAccountId),
        fetchCompanies()
      ]);
      if (!accountDetail) return;
      setDetail(accountDetail);
      return;
    }

    const companyDetail = await fetchDetail(selectedCompany.companyId);
    if (!companyDetail) return;
    setDetail(normalizeCompanyDetail(companyDetail, null));
    await fetchCompanies();
  };

  const getBillingAccountLabel = (account) => {
    if (!account) return '';
    if (account.type === 'COMPANY_MAIN') return `${account.name} · Main`;
    return `${account.name} · Franquicia`;
  };

  const buildBillingAccountPayload = () => ({
    subscriptionPrice: parsedSubscriptionPrice,
    subscriptionStartDate: normalizedDraftSubscriptionStartDate || null,
    billingEmail: normalizedDraftBillingEmail || null,
    fiscalRut: normalizedDraftFiscalRut || null,
    fiscalBusinessName: normalizedDraftFiscalBusinessName || null,
    fiscalAddress: normalizedDraftFiscalAddress || null,
    commercialAgreementNotes: normalizedDraftCommercialAgreementNotes || null
  });

  const saveBillingAccountDraft = async ({ showSuccessToast = true, refresh = true } = {}) => {
    if (!selectedBillingAccountId) return null;
    const updated = await CoreBillingService.updateBillingAccount(selectedBillingAccountId, buildBillingAccountPayload());
    if (showSuccessToast) {
      showToast(
        t('core_charges_toast_billing_account_updated', {
          defaultValue: 'Configuración de Mercado Pago guardada.'
        })
      );
    }
    if (refresh) {
      await handleSelectBillingAccount(selectedBillingAccountId, true);
    }
    return updated;
  };

  const handleSendSubscriptionLink = async () => {
    if (!selectedBillingAccountId) return;
    setSendingSubscriptionLink(true);
    try {
      const response = await CoreBillingService.sendSubscriptionLinkByBillingAccount(selectedBillingAccountId);
      const checkoutUrl = response?.checkoutUrl || '';
      showToast(t('core_charges_toast_subscription_email_sent'));
      await Promise.all([
        handleSelectBillingAccount(selectedBillingAccountId, true),
        fetchCompanies()
      ]);
      setSubscriptionCheckoutUrl(checkoutUrl);
    } catch (e) {
      showToast(getApiErrorMessage(e, t('core_charges_error_sending_link')), 'error');
    } finally {
      setSendingSubscriptionLink(false);
    }
  };

  const handleCreateAndCopySubscriptionLink = async () => {
    if (!selectedBillingAccountId) return;
    setCreatingSubscriptionLink(true);
    try {
      const response = await CoreBillingService.createSubscriptionLinkByBillingAccount(selectedBillingAccountId);
      const checkoutUrl = response?.checkoutUrl || '';
      if (!checkoutUrl) {
        throw new Error(t('core_charges_error_missing_subscription_link', { defaultValue: 'No se pudo obtener el link de suscripción' }));
      }
      await navigator.clipboard.writeText(checkoutUrl);
      showToast(t('core_charges_toast_subscription_link_copied', { defaultValue: 'Link de suscripción copiado.' }));
      await Promise.all([
        handleSelectBillingAccount(selectedBillingAccountId, true),
        fetchCompanies()
      ]);
      setSubscriptionCheckoutUrl(checkoutUrl);
    } catch (e) {
      showToast(getApiErrorMessage(e, t('core_charges_error_copy_link')), 'error');
    } finally {
      setCreatingSubscriptionLink(false);
    }
  };

  const handleSaveBillingAccount = async () => {
    if (!selectedBillingAccountId) return;
    setSavingBillingAccount(true);
    try {
      await saveBillingAccountDraft();
    } catch (e) {
      showToast(
        getApiErrorMessage(
          e,
          t('core_charges_error_update_billing_account', {
            defaultValue: 'Error actualizando la configuración de Mercado Pago'
          })
        ),
        'error'
      );
    } finally {
      setSavingBillingAccount(false);
    }
  };

  const handleSyncMercadoPagoStatuses = async () => {
    if (!selectedBillingAccountId) return;
    setSyncingMercadoPagoStatuses(true);
    try {
      const result = await CoreBillingService.reconcileMercadoPagoStatuses(selectedBillingAccountId);
      const invalidPaymentsReverted =
        Number(result?.preSubscriptionInvoicesReverted || 0) +
        Number(result?.untraceablePaidInvoicesReverted || 0);
      const reconcileErrors = result?.errors || [];
      if (reconcileErrors.length > 0) {
        // The backend now isolates per-account failures instead of aborting silently:
        // surface them here so a broken sync is never mistaken for "nothing to do".
        showToast(
          t('core_charges_toast_mp_statuses_error', {
            defaultValue: `No se pudo conciliar esta cuenta: ${reconcileErrors[0]?.error || 'error desconocido'}`
          }),
          'error'
        );
      } else {
        showToast(
          t('core_charges_toast_mp_statuses_synced', {
            defaultValue: `Mercado Pago conciliado: ${result?.paidInvoicesSynced || 0} cobro(s) verificado(s), ${result?.duplicateInvoicesRepaired || 0} período(s) corregido(s), ${result?.duplicateTransactionsVoided || 0} movimiento(s) duplicado(s) anulado(s) y ${invalidPaymentsReverted} cobro(s) sin respaldo o anteriores al alta revertido(s).`
          })
        );
      }
      await Promise.all([
        handleSelectBillingAccount(selectedBillingAccountId, true),
        fetchCompanies(),
        fetchTransactions(selectedBillingAccountId)
      ]);
    } catch (e) {
      showToast(
        getApiErrorMessage(
          e,
          t('core_charges_error_sync_mp_statuses', {
            defaultValue: 'Error actualizando estados de Mercado Pago'
          })
        ),
        'error'
      );
    } finally {
      setSyncingMercadoPagoStatuses(false);
    }
  };

  // Lets an operator paste a preapproval ID found directly in the Mercado Pago dashboard
  // and attach it to the currently selected billing account, without waiting for that
  // preapproval to surface on its own in the "unresolved" discovery list (e.g. when the
  // account is already linked to a stale/wrong preapproval and needs to be repointed).
  const handleOpenManualLinkDialog = () => {
    setManualLinkPreapprovalId('');
    setPaymentLookupId('');
    setPaymentLookupResult(null);
    setManualLinkDialogOpen(true);
  };

  const handleCloseManualLinkDialog = () => {
    if (manualLinkSubmitting) return;
    setManualLinkDialogOpen(false);
  };

  // Mercado Pago's own dashboard shows a payment's "N.º de operación" freely, but never the
  // preapproval (subscription) id it belongs to — there's no way from their UI alone to go
  // from "I see this charge" to "this is the id I need to paste below". This looks it up
  // through the backend instead, which already knows how to ask Mercado Pago for it.
  const handleLookupPaymentPreapproval = async () => {
    const paymentId = paymentLookupId.trim();
    if (!paymentId) return;
    setPaymentLookupLoading(true);
    setPaymentLookupResult(null);
    try {
      const result = await CoreBillingService.lookupMercadoPagoPaymentPreapproval(paymentId);
      setPaymentLookupResult(result);
      if (result?.preapprovalId) {
        setManualLinkPreapprovalId(result.preapprovalId);
      }
    } catch (e) {
      showToast(getApiErrorMessage(e, 'No se pudo consultar ese pago en Mercado Pago.'), 'error');
    } finally {
      setPaymentLookupLoading(false);
    }
  };

  // Cross-company pivot of "Períodos mensuales": pick a month, see every company's state for
  // it in one table, instead of opening each company to check whether it paid that month.
  const handleLoadMonthlyOverview = async (year = monthlyOverviewYear, month = monthlyOverviewMonth) => {
    setLoadingMonthlyOverview(true);
    try {
      const rows = await CoreBillingService.getMonthlyOverview(year, month);
      setMonthlyOverviewRows(rows);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'No se pudo cargar el estado del mes.'), 'error');
    } finally {
      setLoadingMonthlyOverview(false);
    }
  };

  const handleMonthlyOverviewMonthChange = (value) => {
    // value comes from a <input type="month"> as "YYYY-MM"
    const [yearStr, monthStr] = value.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    if (!year || !month) return;
    setMonthlyOverviewYear(year);
    setMonthlyOverviewMonth(month);
    handleLoadMonthlyOverview(year, month);
  };

  const handleConfirmManualLink = async () => {
    const preapprovalId = manualLinkPreapprovalId.trim();
    if (!selectedBillingAccountId || !preapprovalId) return;
    setManualLinkSubmitting(true);
    try {
      await CoreBillingService.linkPreapproval(selectedBillingAccountId, preapprovalId);
      showToast('Suscripción vinculada correctamente.');
      setManualLinkDialogOpen(false);
      await Promise.all([
        handleSelectBillingAccount(selectedBillingAccountId, true),
        fetchCompanies(),
        fetchTransactions(selectedBillingAccountId)
      ]);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'No se pudo vincular esa suscripción.'), 'error');
    } finally {
      setManualLinkSubmitting(false);
    }
  };

  const handlePauseSubscription = async () => {
    if (!selectedBillingAccountId) return;
    if (!window.confirm(t('core_charges_confirm_pause_subscription'))) return;
    setUpdatingSubscription(true);
    try {
      await CoreBillingService.pauseSubscriptionByBillingAccount(selectedBillingAccountId);
      showToast(t('core_charges_toast_subscription_paused'));
      await Promise.all([
        handleSelectBillingAccount(selectedBillingAccountId, true),
        fetchCompanies()
      ]);
    } catch (e) {
      showToast(getApiErrorMessage(e, t('core_charges_error_pause_subscription')), 'error');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!selectedBillingAccountId) return;
    if (!window.confirm(t('core_charges_confirm_reactivate_subscription'))) return;
    setUpdatingSubscription(true);
    try {
      await CoreBillingService.reactivateSubscriptionByBillingAccount(selectedBillingAccountId);
      showToast(t('core_charges_toast_subscription_reactivated'));
      await Promise.all([
        handleSelectBillingAccount(selectedBillingAccountId, true),
        fetchCompanies()
      ]);
    } catch (e) {
      showToast(getApiErrorMessage(e, t('core_charges_error_reactivate_subscription')), 'error');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedBillingAccountId) return;
    if (!window.confirm(t('core_charges_confirm_cancel_subscription'))) return;
    setUpdatingSubscription(true);
    try {
      await CoreBillingService.cancelSubscriptionByBillingAccount(selectedBillingAccountId);
      showToast(t('core_charges_toast_subscription_canceled'));
      await Promise.all([
        handleSelectBillingAccount(selectedBillingAccountId, true),
        fetchCompanies()
      ]);
    } catch (e) {
      showToast(getApiErrorMessage(e, t('core_charges_error_cancel_subscription')), 'error');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleGeneratePendingLink = async (invoiceId) => {
    if (!selectedBillingAccountId) return;
    setGeneratingInvoiceId(invoiceId);
    try {
      await CoreBillingService.generatePendingPaymentLinkByBillingAccount(selectedBillingAccountId, invoiceId);
      showToast(t('core_charges_toast_payment_link_generated_visible'));
      await handleSelectBillingAccount(selectedBillingAccountId, true);
    } catch (e) {
      showToast(getApiErrorMessage(e, t('core_charges_error_generate_link')), 'error');
    } finally {
      setGeneratingInvoiceId(null);
    }
  };

  const handleGenerateExtrasLink = async () => {
    if (!selectedBillingAccountId) return;
    setGeneratingExtrasLink(true);
    try {
      await CoreBillingService.generateExtrasPaymentLinkByBillingAccount(selectedBillingAccountId);
      showToast(t('core_charges_toast_payment_link_generated_visible'));
      await Promise.all([
        handleSelectBillingAccount(selectedBillingAccountId, true),
        fetchTransactions(selectedBillingAccountId)
      ]);
    } catch (e) {
      showToast(getApiErrorMessage(e, t('core_charges_error_generate_link')), 'error');
    } finally {
      setGeneratingExtrasLink(false);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    if (!selectedCompany) return;
    setMarkingInvoiceId(invoiceId);
    try {
      await CoreBillingService.markInvoicePaid(selectedCompany.companyId, invoiceId, 'Marcado manual desde admin');
      showToast(t('core_charges_toast_invoice_paid'));
      await refreshCurrentSelection();
    } catch (e) {
      showToast(getApiErrorMessage(e, t('core_charges_error_mark_paid')), 'error');
    } finally {
      setMarkingInvoiceId(null);
    }
  };

  const handleSyncPaidStatus = async (invoiceId) => {
    if (!selectedCompany) return;
    setSyncingInvoiceId(invoiceId);
    try {
      await CoreBillingService.syncInvoicePaidStatus(
        selectedCompany.companyId,
        invoiceId,
        'Estado sincronizado manualmente desde admin'
      );
      showToast(t('core_charges_toast_invoice_status_synced', { defaultValue: 'Estado de factura sincronizado como pago.' }));
      await refreshCurrentSelection();
    } catch (e) {
      showToast(
        getApiErrorMessage(
          e,
          t('core_charges_error_sync_paid_status', { defaultValue: 'Error sincronizando estado de pago' })
        ),
        'error'
      );
    } finally {
      setSyncingInvoiceId(null);
    }
  };

  const handleSaveStatusCorrection = async () => {
    if (!statusCorrection?.invoice || !statusCorrection?.billingAccountId) return;
    const notes = statusCorrection.notes.trim();
    if (!notes) {
      showToast('Indicá el motivo de la corrección para conservar la trazabilidad.', 'error');
      return;
    }
    setSavingStatusCorrection(true);
    try {
      await CoreBillingService.updateInvoiceStatus(statusCorrection.billingAccountId, statusCorrection.invoice.id, {
        status: statusCorrection.status,
        notes
      });
      setStatusCorrection(null);
      showToast('Estado del período corregido y registrado en el historial.');
      if (statusCorrection.source === 'monthly') {
        await handleLoadMonthlyOverview();
      } else {
        await refreshCurrentSelection();
      }
    } catch (error) {
      showToast(getApiErrorMessage(error, 'No se pudo corregir el estado del período.'), 'error');
    } finally {
      setSavingStatusCorrection(false);
    }
  };

  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast(t('core_charges_toast_link_copied'));
    } catch (e) {
      showToast(t('core_charges_error_copy_link'), 'error');
    }
  };

  const handleOpenInvoicePdf = async (invoiceId) => {
    if (!selectedCompany) return;
    setOpeningInvoiceId(invoiceId);
    try {
      const blob = await CoreBillingService.getInvoicePdf(selectedCompany.companyId, invoiceId);
      const fileUrl = window.URL.createObjectURL(blob);
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(fileUrl), 60_000);
    } catch (e) {
      showToast(getApiErrorMessage(e, t('core_charges_error_open_invoice')), 'error');
    } finally {
      setOpeningInvoiceId(null);
    }
  };

  const handleOpenInvoiceMenu = (event, invoiceId) => {
    setInvoiceMenuAnchorEl(event.currentTarget);
    setInvoiceMenuInvoiceId(invoiceId);
  };

  const handleCloseInvoiceMenu = () => {
    setInvoiceMenuAnchorEl(null);
    setInvoiceMenuInvoiceId(null);
  };

  const handleInvoiceMenuAction = async (action) => {
    const invoiceId = invoiceMenuInvoiceId;
    handleCloseInvoiceMenu();
    if (!invoiceId) return;

    if (action === 'markPaid') {
      await handleMarkPaid(invoiceId);
      return;
    }

    if (action === 'syncPaidStatus') {
      await handleSyncPaidStatus(invoiceId);
      return;
    }

    if (action === 'correctStatus') {
      const invoice = invoicesById[invoiceId];
      if (invoice) {
        setStatusCorrection({ invoice, status: invoice.status, notes: '' });
      }
      return;
    }

    if (action === 'generateLink') {
      await handleGeneratePendingLink(invoiceId);
    }
  };

  const handleBackToCompanies = () => {
    setSelectedCompany(null);
    setBillingAccounts([]);
    setSelectedBillingAccountId(null);
    setDetail(null);
    setTransactions([]);
    setDetailTab(0);
    setStatusCorrection(null);
  };

  const showingCompanyDetail = Boolean(selectedCompany && detail);

  return (
    <Box p={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} sx={{ display: showingCompanyDetail ? 'none' : 'flex' }}>
        <PageHeader title={t('core_charges_title')} subtitle={t('core_charges_subtitle')} />
      </Stack>
      <Tabs
        value={cobrosViewMode}
        onChange={(_, value) => setCobrosViewMode(value)}
        sx={{ mb: 2, display: showingCompanyDetail ? 'none' : 'flex' }}
      >
        <Tab value="company" label="Por empresa" />
        <Tab value="monthly" label="Por mes" />
      </Tabs>
      <Box>
        <Card sx={{ display: !showingCompanyDetail && cobrosViewMode === 'company' ? 'block' : 'none' }}>
          <CardHeader
            title="Empresas"
            subheader="Elegí una empresa para abrir su detalle de suscripción y cobros."
            action={
              <TextField
                sx={{ width: { xs: 260, md: 360 } }}
                size="small"
                label={t('core_charges_search_company')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            }
            sx={{
              alignItems: 'center',
              '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
              '@media (max-width: 600px)': {
                alignItems: 'stretch',
                flexDirection: 'column',
                '& .MuiCardHeader-action': { mt: 2, width: '100%' },
                '& .MuiTextField-root': { width: '100%' }
              }
            }}
          />
          <CardContent sx={{ pt: 0 }}>
          <List
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
              gap: 1,
              p: 0
            }}
          >
            {companies.map((company) => {
              const hasPendingBalance = company.balanceStatus === 'PENDING_BALANCE';
              return (
              <ListItemButton
                key={company.companyId}
                selected={selectedCompany?.companyId === company.companyId}
                onClick={() => handleSelectCompany(company)}
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  alignItems: 'flex-start',
                  minHeight: 86,
                  borderRadius: 1,
                  px: 1.5,
                  py: 1.15,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.14)'
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.2)'
                  }
                }}
              >
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.15, mb: 0 }}>
                      {company.companyName}
                    </Typography>
                    {company.syncHealth === 'NEEDS_ATTENTION' && (
                      <Tooltip title={company.syncHealthDetail || 'Requiere revisión de sincronización con Mercado Pago'}>
                        <WarningAmberIcon color="warning" sx={{ fontSize: '1.05rem' }} />
                      </Tooltip>
                    )}
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.15 }}>
                      {`${t('core_charges_movements')}: ${company.transactionCount} ·`}
                    </Typography>
                    <Tooltip
                      title={hasPendingBalance
                        ? t('core_charges_balance_pending_tooltip', { count: company.pendingInvoiceCount })
                        : ''}
                    >
                      <Chip
                        size="small"
                        label={hasPendingBalance ? t('core_charges_balance_pending') : t('core_charges_balance_up_to_date')}
                        color={hasPendingBalance ? 'error' : 'success'}
                        sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem', fontWeight: 700 } }}
                      />
                    </Tooltip>
                  </Stack>
                </Box>
              </ListItemButton>
              );
            })}
          </List>
          </CardContent>
        </Card>

        <Card sx={{ display: !showingCompanyDetail && cobrosViewMode === 'monthly' ? 'block' : 'none' }}>
          <CardHeader
            title="Vista mensual"
            subheader="Estado de cobro de todas las empresas para un mes específico."
            action={
              <Stack direction="row" alignItems="center" spacing={2}>
                <TextField
                  type="month"
                  label="Mes"
                  size="small"
                  value={`${monthlyOverviewYear}-${String(monthlyOverviewMonth).padStart(2, '0')}`}
                  onChange={(e) => e.target.value && handleMonthlyOverviewMonthChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                {loadingMonthlyOverview && <CircularProgress size={20} />}
              </Stack>
            }
            sx={{
              alignItems: 'center',
              '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
              '@media (max-width: 600px)': {
                alignItems: 'stretch',
                flexDirection: 'column',
                '& .MuiCardHeader-action': { mt: 2, width: '100%' }
              }
            }}
          />
          <CardContent sx={{ pt: 0 }}>
            {!loadingMonthlyOverview && monthlyOverviewRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No hay empresas para mostrar.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Empresa</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Importe</TableCell>
                      <TableCell>Vencimiento</TableCell>
                      <TableCell>Pago</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlyOverviewRows.map((row) => {
                      const statusBadge = getInvoiceStatusBadge(row.status);
                      return (
                        <TableRow key={row.companyId} hover>
                          <TableCell>{row.companyName}</TableCell>
                          <TableCell>
                            <Chip size="small" label={getStatusLabel(row.status)} color={statusBadge.color} />
                          </TableCell>
                          <TableCell align="right">
                            {row.amount != null ? `${formatMoney(Number(row.amount))}` : '—'}
                          </TableCell>
                          <TableCell>{row.dueDate ? formatSubscriptionDate(row.dueDate) : '—'}</TableCell>
                          <TableCell>{row.paidAt ? formatMovementDateTime(row.paidAt) : '—'}</TableCell>
                          <TableCell align="right">
                            {row.invoiceId ? (
                              <Tooltip title="Corregir el estado de este período a mano (no verifica nada contra Mercado Pago, solo actualiza el estado y queda registrado el motivo)">
                                <span>
                                  <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<EditIcon fontSize="small" />}
                                    disabled={savingStatusCorrection}
                                    onClick={() => setStatusCorrection({
                                      invoice: { id: row.invoiceId, periodMonth: monthlyOverviewMonth, periodYear: monthlyOverviewYear },
                                      status: row.status,
                                      notes: '',
                                      billingAccountId: row.billingAccountId,
                                      source: 'monthly'
                                    })}
                                  >
                                    Corregir
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Paper sx={{ p: { xs: 1.5, md: 2.5 }, display: showingCompanyDetail ? 'block' : 'none' }}>
          {!selectedCompany || !detail ? (
            <Typography variant="body1">{t('core_charges_select_company')}</Typography>
          ) : (
            <>
              <Box sx={{ mb: 2.5 }}>
                <Button size="small" onClick={handleBackToCompanies} sx={{ mb: 1, ml: -1 }}>
                  ← Volver a empresas
                </Button>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{detail.companyName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Gestión de suscripciones, períodos y movimientos de cobro.
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color={getSubscriptionStatusBadge(detail.subscriptionStatus).color}
                    label={`${t('core_charges_subscription')}: ${getStatusLabel(detail.subscriptionStatus)}`}
                  />
                </Stack>
              </Box>

              {billingAccounts.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  {billingAccounts.map((account) => (
                    <Button
                      key={account.id}
                      size="small"
                      variant={selectedBillingAccountId === account.id ? 'contained' : 'outlined'}
                      onClick={() => handleSelectBillingAccount(account.id)}
                    >
                      {getBillingAccountLabel(account)}
                    </Button>
                  ))}
                </Stack>
              )}

              <Tabs value={detailTab} onChange={(_, value) => setDetailTab(value)} sx={{ mb: 2 }}>
                <Tab label="Configuración" />
                <Tab label="Períodos mensuales" />
                <Tab label="Beneficios" />
              </Tabs>

              {detailTab === 0 && (
                <>
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {t('core_charges_subscription_config', { defaultValue: 'Configuración de suscripción' })}
                    </Typography>
                    <Stack spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('core_charges_billing_email', { defaultValue: 'Email administrativo / billing' })}
                        value={billingEmailDraft}
                        onChange={(e) => setBillingEmailDraft(e.target.value)}
                        error={billingEmailInvalid}
                        helperText={
                          billingEmailInvalid
                            ? t('core_charges_billing_email_invalid', { defaultValue: 'Email inválido.' })
                            : t('core_charges_billing_email_helper', {
                                defaultValue: 'A este email le llegan avisos y facturas.'
                              })
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t('core_charges_subscription_price', { defaultValue: 'Precio mensual' })}
                        value={subscriptionPriceDraft}
                        onChange={(e) => setSubscriptionPriceDraft(e.target.value)}
                        error={subscriptionPriceInvalid}
                        helperText={
                          subscriptionPriceInvalid
                            ? t('core_charges_subscription_price_invalid', {
                                defaultValue: 'Ingresá un precio válido.'
                              })
                            : t('core_charges_subscription_price_helper', {
                                defaultValue: 'Este precio se usa solo para la suscripción de esta cuenta.'
                              })
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Fecha de inicio del acuerdo"
                        value={subscriptionStartDateDraft}
                        onChange={(e) => setSubscriptionStartDateDraft(normalizeSubscriptionStartDate(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                        helperText="Define los períodos de servicio. Si queda vacía, se usa la fecha de creación del acuerdo."
                      />
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('core_charges_fiscal_rut', { defaultValue: 'RUT' })}
                          value={fiscalRutDraft}
                          onChange={(e) => setFiscalRutDraft(e.target.value)}
                          helperText={t('core_charges_optional', { defaultValue: 'Opcional' })}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={t('core_charges_fiscal_business_name', { defaultValue: 'Razón social' })}
                          value={fiscalBusinessNameDraft}
                          onChange={(e) => setFiscalBusinessNameDraft(e.target.value)}
                          helperText={t('core_charges_optional', { defaultValue: 'Opcional' })}
                        />
                      </Stack>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('core_charges_fiscal_address', { defaultValue: 'Dirección fiscal' })}
                        value={fiscalAddressDraft}
                        onChange={(e) => setFiscalAddressDraft(e.target.value)}
                        helperText={t('core_charges_optional', { defaultValue: 'Opcional' })}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={3}
                        label={t('core_charges_commercial_agreement_notes', { defaultValue: 'Notas del acuerdo' })}
                        value={commercialAgreementNotesDraft}
                        onChange={(e) => setCommercialAgreementNotesDraft(e.target.value)}
                        helperText={t('core_charges_optional', { defaultValue: 'Opcional' })}
                      />
                      {subscriptionAlreadyAttached ? (
                        <Alert severity="info">
                          {t('core_charges_subscription_already_attached', {
                            defaultValue: 'Esta cuenta ya tiene una suscripción asociada en Mercado Pago.'
                          })}
                        </Alert>
                      ) : canSaveBillingAccount ? (
                        <Alert severity="info">
                          {t('core_charges_save_before_subscription_link', {
                            defaultValue: 'Guardá la configuración antes de generar o enviar el link de suscripción.'
                          })}
                        </Alert>
                      ) : hasValidSubscriptionPrice ? (
                        <Alert severity="info">
                          {t('core_charges_subscription_plan_link_ready', {
                            defaultValue: 'El link de suscripción se crea como plan de Mercado Pago y no requiere email del pagador.'
                          })}
                        </Alert>
                      ) : (
                        <Alert severity="warning">
                          {t('core_charges_subscription_price_missing', {
                            defaultValue: 'Definí y guardá un precio mensual mayor a cero antes de generar el link.'
                          })}
                        </Alert>
                      )}
                      <Stack direction="row" justifyContent="flex-end">
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          loading={savingBillingAccount}
                          disabled={!canSaveBillingAccount}
                          onClick={handleSaveBillingAccount}
                        >
                          {t('core_charges_save_subscription_config', { defaultValue: 'Guardar configuración' })}
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Suscripción</Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <LoadingButton size="small" variant="contained" loading={sendingSubscriptionLink} disabled={!canSendSubscriptionLink} onClick={handleSendSubscriptionLink}>
                        {t('core_charges_send_subscription_link')}
                      </LoadingButton>
                      <LoadingButton size="small" variant="outlined" loading={creatingSubscriptionLink} disabled={!canCreateSubscriptionLink} onClick={handleCreateAndCopySubscriptionLink} startIcon={<ContentCopyIcon fontSize="small" />}>
                        {t('core_charges_copy_subscription_link', { defaultValue: 'Copiar link suscripción' })}
                      </LoadingButton>
                      {subscriptionCheckoutUrl && <Tooltip title={subscriptionCheckoutUrl}><Button size="small" variant="text" onClick={() => handleCopyLink(subscriptionCheckoutUrl)} startIcon={<ContentCopyIcon fontSize="small" />}>{t('core_charges_copy_last_subscription_link', { defaultValue: 'Copiar último link' })}</Button></Tooltip>}
                      {detail.subscriptionStatus === 'ACTIVE' && <LoadingButton size="small" variant="outlined" loading={updatingSubscription} disabled={saving || !subscriptionAccepted || !accountSupportsSubscription} onClick={handlePauseSubscription}>{t('core_charges_pause_subscription')}</LoadingButton>}
                      {(detail.subscriptionStatus === 'PAUSED' || detail.subscriptionStatus === 'PENDING') && <LoadingButton size="small" variant="outlined" loading={updatingSubscription} disabled={saving || !subscriptionAccepted || !accountSupportsSubscription} onClick={handleReactivateSubscription}>{t('core_charges_reactivate_subscription')}</LoadingButton>}
                      {(detail.subscriptionStatus === 'ACTIVE' || detail.subscriptionStatus === 'PAUSED' || detail.subscriptionStatus === 'PENDING') && <LoadingButton size="small" color="error" variant="outlined" loading={updatingSubscription} disabled={saving || !subscriptionAccepted || !accountSupportsSubscription} onClick={handleCancelSubscription}>{t('core_charges_cancel_subscription')}</LoadingButton>}
                    </Stack>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>Estado de sincronización con Mercado Pago</Typography>
                      {detail.syncHealth === 'NEEDS_ATTENTION' ? (
                        <Chip size="small" icon={<WarningAmberIcon />} label="Necesita revisión" color="warning" />
                      ) : (
                        <Chip size="small" label="Al día" color="success" variant="outlined" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Consulta los pagos reales en Mercado Pago, corrige períodos asignados y anula movimientos internos duplicados.
                    </Typography>
                    {detail.syncHealth === 'NEEDS_ATTENTION' && detail.syncHealthDetail && (
                      <Alert severity="warning" sx={{ mb: 1.5 }}>{detail.syncHealthDetail}</Alert>
                    )}
                    <Stack spacing={0.25} sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Suscripción vinculada (preapproval ID): {detail.mpPreapprovalId || 'ninguna'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Última verificación exitosa: {detail.lastReconcileCheckAt ? formatSubscriptionDate(detail.lastReconcileCheckAt) : 'nunca'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <LoadingButton size="small" variant="contained" loading={syncingMercadoPagoStatuses} disabled={saving || updatingSubscription || syncingMercadoPagoStatuses} onClick={handleSyncMercadoPagoStatuses}>Conciliar y reparar</LoadingButton>
                    </Stack>
                    {detail.syncHealth === 'NEEDS_ATTENTION' && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Arreglo manual
                        </Typography>
                        <Button size="small" variant="outlined" color="warning" disabled={saving || updatingSubscription} onClick={handleOpenManualLinkDialog}>
                          Vincular por ID manualmente
                        </Button>
                      </>
                    )}
                  </Paper>

                  {/* El historial de facturas/pagos se muestra en Movimientos. Conservamos este bloque
                      oculto porque sus acciones individuales siguen siendo usadas por el menú. */}
                  <Box sx={{ display: 'none' }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('core_charges_invoices')}</Typography>
                  {detail.invoices.length === 0 ? (
                    <Typography variant="body2">{t('core_charges_no_invoices')}</Typography>
                  ) : (
                    <List dense>
                      {detail.invoices.map((inv) => {
                        const invoiceStatusBadge = getInvoiceStatusBadge(inv.status);
                        const canMarkPaid = inv.status !== 'PAID';
                        const canGenerateLink =
                          inv.status === 'REJECTED_FINAL' || (inv.source === 'HISTORICAL' && inv.status === 'PENDING');
                        const hasInvoiceActions = canMarkPaid || canGenerateLink;
                        const invoiceActionBusy =
                          markingInvoiceId === inv.id ||
                          syncingInvoiceId === inv.id ||
                          generatingInvoiceId === inv.id;
                        return (
                        <Box key={inv.id}>
                          <Box sx={{ py: 1 }}>
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', md: 'center' }}
                              spacing={1}
                            >
                              <Box sx={{ minWidth: 0, pr: { md: 2 }, flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {`${inv.periodMonth}/${inv.periodYear} · ${formatMoney(Number(inv.amount), inv.currency)}`}
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.3 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {`${t('core_charges_status')}:`}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={getStatusLabel(invoiceStatusBadge.status)}
                                    color={invoiceStatusBadge.color}
                                    sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem', fontWeight: 700 } }}
                                  />
                                </Stack>
                              </Box>
                              <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap' }}>
                                {Boolean(inv.externalDocumentId) && (
                                  <Tooltip title={t('core_charges_open_invoice')}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        disabled={openingInvoiceId === inv.id}
                                        onClick={() => handleOpenInvoicePdf(inv.id)}
                                      >
                                        {openingInvoiceId === inv.id ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <ReceiptLongIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                {hasInvoiceActions && (
                                  <Tooltip title={t('core_charges_actions', { defaultValue: 'Acciones' })}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={(event) => handleOpenInvoiceMenu(event, inv.id)}
                                        disabled={saving || updatingSubscription || invoiceActionBusy}
                                      >
                                        {invoiceActionBusy ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <MoreVertIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Stack>
                            </Stack>
                          </Box>
                          <Divider />
                        </Box>
                        );
                      })}
                    </List>
                  )}
                  </Box>

                  {/* Extras de envíos y links manuales ya no forman parte del flujo de cobros actual. */}
                  <Box sx={{ display: 'none' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="subtitle1">Facturas extra (envíos)</Typography>
                    <LoadingButton
                      size="small"
                      variant="contained"
                      loading={generatingExtrasLink}
                      disabled={saving || pendingExtraInvoices.length === 0 || !accountSupportsExtras}
                      onClick={handleGenerateExtrasLink}
                    >
                      {t('core_charges_generate_link')}
                    </LoadingButton>
                  </Stack>
                  {(detail.extraInvoices || []).length === 0 ? (
                    <Typography variant="body2">No hay facturas extra.</Typography>
                  ) : (
                    <List dense>
                      {detail.extraInvoices.map((inv) => {
                        const invoiceStatusBadge = getInvoiceStatusBadge(inv.status);
                        return (
                          <Box key={`extra-${inv.id}`}>
                            <Box sx={{ py: 1 }}>
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                spacing={1}
                              >
                                <Box sx={{ minWidth: 0, pr: { md: 2 }, flex: 1 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {`${inv.periodMonth}/${inv.periodYear} · ${formatMoney(Number(inv.amount), inv.currency)}`}
                                  </Typography>
                                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      {`${t('core_charges_status')}:`}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={getStatusLabel(invoiceStatusBadge.status)}
                                      color={invoiceStatusBadge.color}
                                      sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem', fontWeight: 700 } }}
                                    />
                                  </Stack>
                                </Box>
                              </Stack>
                            </Box>
                            <Divider />
                          </Box>
                        );
                      })}
                    </List>
                  )}

                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>{t('core_charges_payment_links')}</Typography>
                  {openPaymentLinks.length === 0 ? (
                    <Typography variant="body2">{t('core_charges_no_payment_links')}</Typography>
                  ) : (
                    <List dense>
                      {openPaymentLinks.map((link) => {
                        const linkedInvoice = link.invoiceId ? invoicesById[link.invoiceId] : null;
                        const periodLabel = linkedInvoice
                          ? `${String(linkedInvoice.periodMonth).padStart(2, '0')}/${linkedInvoice.periodYear}`
                          : t('core_charges_historical_debt');
                        return (
                        <Box key={link.id}>
                          <Box sx={{ py: 1 }}>
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', md: 'center' }}
                              spacing={1}
                            >
                              <Box sx={{ minWidth: 0, pr: { md: 2 }, flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {`${formatMoney(Number(link.amount), link.currency)} · ${getStatusLabel(link.status)} · ${t('core_charges_period')}: ${periodLabel}`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mt: 0.2,
                                    fontFamily: 'monospace',
                                    overflowWrap: 'anywhere'
                                  }}
                                >
                                  {link.url}
                                </Typography>
                              </Box>
                              <Tooltip title={t('core_charges_copy_link')}>
                                <IconButton size="small" color="primary" onClick={() => handleCopyLink(link.url)}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                          <Divider />
                        </Box>
                        );
                      })}
                    </List>
                  )}

                  {pendingInvoices.length > 0 && (
                    <Alert sx={{ mt: 2 }} severity="warning">
                      {t('core_charges_pending_invoices_alert', { count: pendingInvoices.length })}
                    </Alert>
                  )}
                  </Box>
                </>
              )}

              <Menu
                anchorEl={invoiceMenuAnchorEl}
                open={Boolean(invoiceMenuAnchorEl)}
                onClose={handleCloseInvoiceMenu}
              >
                {invoiceMenuInvoiceId && (() => {
                  const activeInvoice = invoicesById[invoiceMenuInvoiceId];
                  if (!activeInvoice) return null;

                  const canMarkPaid = activeInvoice.status !== 'PAID';
                  const canGenerateLink =
                    activeInvoice.status === 'REJECTED_FINAL' ||
                    (activeInvoice.source === 'HISTORICAL' && activeInvoice.status === 'PENDING');

                  return (
                    <>
                      {canMarkPaid && (
                        <MenuItem onClick={() => handleInvoiceMenuAction('markPaid')}>
                          {t('core_charges_mark_paid')}
                        </MenuItem>
                      )}
                      {canMarkPaid && (
                        <MenuItem onClick={() => handleInvoiceMenuAction('syncPaidStatus')}>
                          {t('core_charges_sync_paid_status_only', { defaultValue: 'Solo sync estado' })}
                        </MenuItem>
                      )}
                      <MenuItem onClick={() => handleInvoiceMenuAction('correctStatus')}>
                        Corregir estado…
                      </MenuItem>
                      {canGenerateLink && (
                        <MenuItem onClick={() => handleInvoiceMenuAction('generateLink')}>
                          {t('core_charges_generate_link')}
                        </MenuItem>
                      )}
                    </>
                  );
                })()}
              </Menu>

              {detailTab === 1 && (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ p: 2, pb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>Períodos de suscripción</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Inicio considerado: {formatSubscriptionDate(selectedBillingAccount?.subscriptionStartDate || selectedBillingAccount?.createdAt)}. Cada fila representa un mes; “Sin registro” no equivale a deuda.
                    </Typography>
                  </Box>
                  {monthlyPeriods.length === 0 ? (
                    <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
                      Configurá la fecha de inicio del acuerdo para comenzar el historial mensual.
                    </Alert>
                  ) : (
                    <>
                      <TableContainer>
                        <Table size="small" aria-label="Períodos mensuales de suscripción">
                          <TableHead>
                            <TableRow>
                              <TableCell>Período</TableCell>
                              <TableCell>Estado</TableCell>
                              <TableCell align="right">Importe</TableCell>
                              <TableCell>Vencimiento</TableCell>
                              <TableCell>Pago</TableCell>
                              <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedMonthlyPeriods.map((period) => {
                              const invoice = period.invoice;
                              const statusBadge = getInvoiceStatusBadge(period.status);
                              return (
                                <TableRow key={period.id} hover>
                                  <TableCell>{period.periodLabel}</TableCell>
                                  <TableCell>
                                    <Chip size="small" label={getStatusLabel(period.status)} color={statusBadge.color} />
                                  </TableCell>
                                  <TableCell align="right">
                                    {invoice ? formatMoney(Number(invoice.amount), invoice.currency) : '—'}
                                  </TableCell>
                                  <TableCell>{invoice?.dueDate ? formatSubscriptionDate(invoice.dueDate) : '—'}</TableCell>
                                  <TableCell>{invoice?.paidAt ? formatMovementDateTime(invoice.paidAt) : '—'}</TableCell>
                                  <TableCell align="right">
                                    {invoice ? (
                                      <Tooltip title="Corregir el estado de este período a mano (no verifica nada contra Mercado Pago, solo actualiza el estado y queda registrado el motivo)">
                                        <span>
                                          <Button
                                            size="small"
                                            variant="text"
                                            disabled={savingStatusCorrection}
                                            startIcon={<EditIcon fontSize="small" />}
                                            onClick={() => setStatusCorrection({ invoice, status: invoice.status, notes: '', billingAccountId: selectedBillingAccountId, source: 'company' })}
                                          >
                                            Corregir
                                          </Button>
                                        </span>
                                      </Tooltip>
                                    ) : '—'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={monthlyPeriods.length}
                        page={monthlyPeriodsPage}
                        rowsPerPage={monthlyPeriodsPageSize}
                        rowsPerPageOptions={MONTHLY_PERIOD_PAGE_SIZES}
                        labelRowsPerPage="Meses por página:"
                        onPageChange={(_event, page) => setMonthlyPeriodsPage(page)}
                        onRowsPerPageChange={(event) => {
                          setMonthlyPeriodsPageSize(Number(event.target.value));
                          setMonthlyPeriodsPage(0);
                        }}
                      />
                    </>
                  )}
                </Paper>
              )}

              {detailTab === 2 && (
                <Stack spacing={2}>
                  <Alert severity="info">
                    Los beneficios se descuentan del próximo período. Las suscripciones actuales no cambian hasta otorgar uno.
                  </Alert>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <TextField select fullWidth size="small" label="Motivo del beneficio" value={benefitReasonDraft} onChange={(e) => setBenefitReasonDraft(e.target.value)}>
                      <MenuItem value="REFERRED_COMPANY">Empresa referida</MenuItem>
                      <MenuItem value="NEW_BUSINESS_UNIT">Nueva unidad</MenuItem>
                      <MenuItem value="SPECIAL_PROMOTION">Promoción especial</MenuItem>
                    </TextField>
                    <TextField fullWidth size="small" type="number" label="Créditos a otorgar (UYU)" value={benefitAmountDraft} onChange={(e) => setBenefitAmountDraft(e.target.value)} />
                    <TextField fullWidth size="small" type="number" label="Máximo por mes (UYU)" value={benefitMonthlyLimitDraft} onChange={(e) => setBenefitMonthlyLimitDraft(e.target.value)} helperText="Vacío conserva el límite ya definido." />
                  </Stack>
                  <TextField fullWidth size="small" label="Motivo" value={benefitDescriptionDraft} onChange={(e) => setBenefitDescriptionDraft(e.target.value)} placeholder="Ej.: Beneficio por referido" />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="subtitle2">Saldo disponible: UYU {Number(detail.benefits?.balance || 0).toLocaleString('es-UY')}</Typography>
                      <Typography variant="body2" color="text.secondary">Próximo descuento: UYU {Number(detail.benefits?.nextDiscount || 0).toLocaleString('es-UY')}</Typography>
                    </Box>
                    <LoadingButton variant="contained" loading={grantingBenefit} onClick={handleGrantBenefit}>Otorgar beneficio</LoadingButton>
                  </Stack>
                  <List dense>
                    {(detail.benefits?.movements || []).map((movement) => (
                      <Box key={movement.id} sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {movement.amount >= 0 ? '+' : ''}{Number(movement.amount).toLocaleString('es-UY')} UYU · {movement.type === 'GRANT' ? 'Beneficio otorgado' : 'Beneficio aplicado'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{movement.description || formatMovementDateTime(movement.createdAt)}</Typography>
                        <Divider sx={{ mt: 1 }} />
                      </Box>
                    ))}
                  </List>
                </Stack>
              )}

              <Dialog
                open={Boolean(statusCorrection)}
                onClose={() => !savingStatusCorrection && setStatusCorrection(null)}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>Corregir estado del período</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {statusCorrection?.invoice && `Período ${String(statusCorrection.invoice.periodMonth).padStart(2, '0')}/${statusCorrection.invoice.periodYear}. La corrección queda registrada y Mercado Pago puede restaurar un pago verificado en la próxima conciliación.`}
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Estado"
                    value={statusCorrection?.status || ''}
                    onChange={(event) => setStatusCorrection((current) => ({ ...current, status: event.target.value }))}
                    sx={{ mb: 2 }}
                  >
                    {MONTHLY_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    required
                    autoFocus
                    multiline
                    minRows={3}
                    label="Motivo de la corrección"
                    value={statusCorrection?.notes || ''}
                    onChange={(event) => setStatusCorrection((current) => ({ ...current, notes: event.target.value }))}
                    helperText="Obligatorio para conservar la trazabilidad del cambio."
                  />
                </DialogContent>
                <DialogActions>
                  <Button disabled={savingStatusCorrection} onClick={() => setStatusCorrection(null)}>Cancelar</Button>
                  <LoadingButton loading={savingStatusCorrection} variant="contained" onClick={handleSaveStatusCorrection}>
                    Guardar corrección
                  </LoadingButton>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={manualLinkDialogOpen}
        onClose={handleCloseManualLinkDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Vincular suscripción por ID</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Mercado Pago no muestra el ID de la suscripción (preapproval) en ningún lado de su panel —
            solo el "N.° de operación" de cada cobro puntual. Si tenés ese número (lo ves en el detalle
            de un pago, en "Actividad" o en "Planes de suscripción → Clientes"), pegalo acá y lo buscamos.
          </Alert>
          <Stack direction="row" spacing={1} sx={{ mb: paymentLookupResult ? 1.5 : 2 }}>
            <TextField
              fullWidth
              size="small"
              label="N.° de operación de Mercado Pago"
              value={paymentLookupId}
              onChange={(e) => setPaymentLookupId(e.target.value)}
            />
            <LoadingButton
              variant="outlined"
              loading={paymentLookupLoading}
              disabled={!paymentLookupId.trim()}
              onClick={handleLookupPaymentPreapproval}
              sx={{ flexShrink: 0 }}
            >
              Buscar
            </LoadingButton>
          </Stack>
          {paymentLookupResult && (
            <Alert severity={paymentLookupResult.preapprovalId ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {paymentLookupResult.preapprovalId ? (
                <>Suscripción encontrada y cargada abajo: {paymentLookupResult.preapprovalId}</>
              ) : (
                <>Ese pago no tiene una suscripción asociada en Mercado Pago (fue un cobro suelto).</>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Estado: {paymentLookupResult.status || '-'} · Pagador: {paymentLookupResult.payerEmail || '-'} · Monto:{' '}
                {paymentLookupResult.transactionAmount != null ? `${formatMoney(Number(paymentLookupResult.transactionAmount))}` : '-'}
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Preapproval ID a vincular con{' '}
            {selectedBillingAccount ? getBillingAccountLabel(selectedBillingAccount) : `esta cuenta (${selectedBillingAccountId})`}.
            {detail?.mpPreapprovalId && ` Esto va a reemplazar la suscripción actualmente vinculada (${detail.mpPreapprovalId}).`}
          </Typography>
          <TextField
            fullWidth
            label="Preapproval ID"
            value={manualLinkPreapprovalId}
            onChange={(e) => setManualLinkPreapprovalId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={manualLinkSubmitting} onClick={handleCloseManualLinkDialog}>Cancelar</Button>
          <LoadingButton
            variant="contained"
            loading={manualLinkSubmitting}
            disabled={!manualLinkPreapprovalId.trim()}
            onClick={handleConfirmManualLink}
          >
            Vincular
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChargesPage;
